import type {
  CapabilityInfo,
  CreativeDirectionSlot,
} from "@/lib/api";

export type SingleDirectionSlot = Exclude<CreativeDirectionSlot, "modifier">;

export interface DirectionPresetSelection {
  id: string;
  title: string;
  slot: CreativeDirectionSlot;
  stable_key?: string | null;
}

export interface DirectionBundleSelection {
  id: string;
  title: string;
  stable_key?: string | null;
}

export interface DirectionSelections {
  genre: string;
  presets: Partial<Record<SingleDirectionSlot, DirectionPresetSelection>>;
  modifiers: DirectionPresetSelection[];
  bundle: DirectionBundleSelection | null;
}

export const EMPTY_DIRECTIONS: DirectionSelections = {
  genre: "General",
  presets: {},
  modifiers: [],
  bundle: null,
};

const IMAGE_SLOTS: SingleDirectionSlot[] = [
  "camera_body",
  "camera_look",
  "lens",
  "lighting",
  "lighting_accent",
  "focal_length",
  "aperture",
  "framing",
  "shot_angle",
];
const VIDEO_SLOTS: SingleDirectionSlot[] = [
  "camera_body",
  "camera_look",
  "lens",
  "lighting",
  "lighting_accent",
  "movement",
  "focal_length",
  "aperture",
  "framing",
  "shot_angle",
];

const QUICK_NAMES = [
  "ratio",
  "aspect_ratio",
  "image_size",
  "resolution",
  "size",
  "quality",
  "duration",
  "max_images",
  "num_images",
  "output_count",
  "n",
  "generate_audio",
] as const;

export function quickParamNames(capability: CapabilityInfo | null): string[] {
  if (!capability) return [];
  return capability.ui_schema.params
    .filter((param) => QUICK_NAMES.includes(param.name as (typeof QUICK_NAMES)[number]))
    .map((param) => param.name);
}

export function directionsForModality(
  selections: DirectionSelections,
  modality: "image" | "video" | "audio"
): DirectionSelections {
  if (modality === "audio") {
    return { ...selections, presets: {}, modifiers: [], bundle: null };
  }
  const allowed = modality === "image" ? IMAGE_SLOTS : VIDEO_SLOTS;
  return {
    ...selections,
    bundle: null,
    presets: Object.fromEntries(
      Object.entries(selections.presets).filter(([slot]) => allowed.includes(slot as SingleDirectionSlot))
    ),
  };
}

export function directionLookLabel(selections: DirectionSelections): string {
  return selections.presets.lighting?.title
    ?? selections.presets.camera_look?.title
    ?? selections.presets.camera_body?.title
    ?? "Auto";
}

export function directionCameraLabel(
  selections: DirectionSelections,
  modality: "image" | "video" | "audio"
): string {
  if (modality === "audio") return "Not available";
  const framing = [
    selections.presets.camera_body,
    selections.presets.movement,
    selections.presets.lens,
    selections.presets.focal_length,
    selections.presets.aperture,
    selections.presets.framing,
    selections.presets.shot_angle,
  ].filter((selection): selection is DirectionPresetSelection => Boolean(selection));
  if (!framing.length) return "Auto";
  if (framing.length === 1) return framing[0].title;
  return `${framing[0].title} +${framing.length - 1}`;
}

export function selectedDirectionCount(selections: DirectionSelections): number {
  return Object.keys(selections.presets).length + selections.modifiers.length;
}

export function resetVisualDirections(selections: DirectionSelections): DirectionSelections {
  return { ...selections, presets: {}, modifiers: [], bundle: null };
}

export function directionsFromParams(params: Record<string, unknown>): DirectionSelections {
  const request = params.creative_direction && typeof params.creative_direction === "object"
    ? params.creative_direction as Record<string, unknown>
    : {};
  const snapshot = params.creative_direction_resolved && typeof params.creative_direction_resolved === "object"
    ? params.creative_direction_resolved as Record<string, unknown>
    : {};
  const presets: DirectionSelections["presets"] = {};
  const modifiers: DirectionPresetSelection[] = [];
  const rows = Array.isArray(snapshot.slots) ? snapshot.slots : [];

  for (const raw of rows) {
    if (!raw || typeof raw !== "object") continue;
    const row = raw as Record<string, unknown>;
    const slot = row.slot;
    if (!isCreativeDirectionSlot(slot) || typeof row.item_id !== "string" || typeof row.title !== "string") continue;
    const selection: DirectionPresetSelection = {
      id: row.item_id,
      title: row.title,
      slot,
      stable_key: typeof row.stable_key === "string" ? row.stable_key : null,
    };
    if (slot === "modifier") modifiers.push(selection);
    else presets[slot] = selection;
  }

  const bundleSnapshot = snapshot.bundle && typeof snapshot.bundle === "object"
    ? snapshot.bundle as Record<string, unknown>
    : null;
  const bundleId = typeof request.bundle_id === "string" ? request.bundle_id : null;
  const bundle = bundleId && bundleSnapshot && typeof bundleSnapshot.title === "string"
    ? {
        id: bundleId,
        title: bundleSnapshot.title,
        stable_key: typeof bundleSnapshot.stable_key === "string" ? bundleSnapshot.stable_key : null,
      }
    : null;
  const genre = typeof request.genre === "string" && request.genre.trim()
    ? request.genre
    : typeof snapshot.genre === "string" && snapshot.genre.trim()
      ? snapshot.genre
      : "General";
  return { genre, presets, modifiers: modifiers.slice(0, 3), bundle };
}

function isCreativeDirectionSlot(value: unknown): value is CreativeDirectionSlot {
  return [
    "camera_body", "camera_look", "lens", "focal_length", "aperture",
    "movement", "shot_angle", "framing", "lighting", "lighting_accent", "modifier",
  ].includes(String(value));
}

const SERVER_OWNED_DIRECTION_PARAMS = [
  "creative_direction",
  "creative_direction_resolved",
  "prompt_manual",
  "camera_manual",
  "lighting_manual",
];

function withoutDirectionPayload(params: Record<string, unknown>): Record<string, unknown> {
  const present = SERVER_OWNED_DIRECTION_PARAMS.filter((name) => name in params);
  if (!present.length) return params;
  const next = { ...params };
  for (const name of present) delete next[name];
  return next;
}

export function withCreativeDirection(
  params: Record<string, unknown>,
  selections: DirectionSelections,
  modality: "image" | "video" | "audio" = "image"
): Record<string, unknown> {
  if (modality === "audio") {
    const base = withoutDirectionPayload(params);
    if (
      selections.genre === "General"
      || typeof base.prompt !== "string"
      || !base.prompt.trim()
    ) return base;
    return {
      ...base,
      prompt: `${base.prompt.trim()}\n\nCreative direction: ${selections.genre} genre.`,
    };
  }

  const presetIds = Object.fromEntries(
    Object.entries(selections.presets)
      .filter((entry): entry is [SingleDirectionSlot, DirectionPresetSelection] => Boolean(entry[1]?.id))
      .map(([slot, selection]) => [slot, selection.id])
  ) as Partial<Record<SingleDirectionSlot, string>>;
  if (selections.modifiers.length) {
    Object.assign(presetIds, {
      modifier: selections.modifiers.map((selection) => selection.id),
    });
  }

  const genre = selections.genre !== "General" ? selections.genre : undefined;
  if (!genre && !Object.keys(presetIds).length && !selections.bundle) {
    return withoutDirectionPayload(params);
  }

  return {
    ...params,
    creative_direction: {
      version: 1,
      ...(genre ? { genre } : {}),
      ...(selections.bundle ? { bundle_id: selections.bundle.id } : {}),
      preset_ids: presetIds,
    },
  };
}
